import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CosmicIdentityHeader } from "@/components/CosmicIdentityHeader";
import { DailyCurrentCard } from "@/components/DailyCurrentCard";
import { OracleSection } from "@/components/OracleSection";
import { TwelveHousesCarousel } from "@/components/TwelveHousesCarousel";
import { OrbitSection } from "@/components/OrbitSection";
import { DreamSanctuary } from "@/components/DreamSanctuary";
import { StarfieldBackground } from "@/components/StarfieldBackground";
import { WelcomeOmen } from "@/components/WelcomeOmen";
import ArchitectOfYear from "@/components/ArchitectOfYear";
import SettingsPanel from "@/components/SettingsPanel";
import OracleAdminDashboard from "@/components/OracleAdminDashboard";
import NotificationPrompt from "@/components/NotificationPrompt";
import CelestialMessenger from "@/components/CelestialMessenger";
import TransitHorizon from "@/components/TransitHorizon";
import NatalChart from "@/components/NatalChart";
import { LazySection } from "@/components/LazySection";
import { useNavigation, useBackHandler, ROUTES } from "@/contexts/NavigationContext";
import { useLanguage } from "@/contexts/LanguageContext";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

/**
 * SanctuaryDashboard - The All-in-One personal astrology dashboard
 * Netflix-style horizontal layout with compact, feature-rich sections
 * 
 * PERFORMANCE OPTIMIZED:
 * - Lazy loading for below-the-fold sections
 * - Pre-generation of content at login
 * - Single API call for cached data
 * 
 * Deep Linking: Accepts onNavigateToSection for URL-based navigation
 */
export const SanctuaryDashboard = ({ userData, onOpenChat, onReset, onLogout, isAuthenticated, onNavigateToSection, onAvatarChange }) => {
    const [showSettings, setShowSettings] = useState(false);
    const [showAdminDashboard, setShowAdminDashboard] = useState(false);
    const [showNatalChart, setShowNatalChart] = useState(false);
    const [localAvatarUrl, setLocalAvatarUrl] = useState(userData?.avatarUrl);
    const { pushRoute } = useNavigation();
    const { language } = useLanguage();
    const isSpanish = language === 'es';
    
    // Pre-generate content on mount (fire-and-forget)
    useEffect(() => {
        const pregenerateContent = async () => {
            try {
                const langParam = language?.startsWith('es') ? 'es' : 'en';
                fetch(`${API_URL}/api/user/pregenerate-content?lang=${langParam}`, {
                    method: 'POST',
                    credentials: 'include'
                }).catch(() => {}); // Fire and forget
            } catch {}
        };
        
        pregenerateContent();
    }, [language]);
    
    // Sync local avatar with userData prop
    useEffect(() => {
        setLocalAvatarUrl(userData?.avatarUrl);
    }, [userData?.avatarUrl]);
    
    // Listen for openNatalChart event from Settings panel
    useEffect(() => {
        const handleOpenNatalChart = () => {
            setShowNatalChart(true);
        };
        window.addEventListener('openNatalChart', handleOpenNatalChart);
        return () => window.removeEventListener('openNatalChart', handleOpenNatalChart);
    }, []);
    
    // Handle avatar change from settings panel
    const handleAvatarChange = (newAvatarUrl) => {
        setLocalAvatarUrl(newAvatarUrl);
        if (onAvatarChange) {
            onAvatarChange(newAvatarUrl);
        }
    };
    
    // Handle settings open with deep link
    const handleOpenSettings = () => {
        setShowSettings(true);
        pushRoute(ROUTES.SETTINGS);
    };
    
    // Handle settings close with URL update
    const handleCloseSettings = useCallback(() => {
        setShowSettings(false);
        pushRoute(ROUTES.DASHBOARD, { replace: true });
    }, [pushRoute]);
    
    // Register back handler for settings panel - memoized to prevent infinite loops
    const settingsBackHandler = useCallback(() => {
        if (showSettings) {
            handleCloseSettings();
            return true;
        }
        return false;
    }, [showSettings, handleCloseSettings]);
    
    useBackHandler(settingsBackHandler, 5);

    return (
        <div className="relative min-h-screen">
            {/* Starfield Background */}
            <StarfieldBackground />
            
            {/* Settings Panel Modal */}
            <AnimatePresence>
                {showSettings && (
                    <SettingsPanel 
                        isOpen={showSettings} 
                        onClose={handleCloseSettings}
                        onReset={onReset}
                        onLogout={onLogout}
                        isGuest={!isAuthenticated || userData?.isAnonymous}
                        onAvatarChange={handleAvatarChange}
                        onOpenAdmin={() => setShowAdminDashboard(true)}
                    />
                )}
            </AnimatePresence>
            
            {/* Admin Dashboard Modal */}
            <AnimatePresence>
                {showAdminDashboard && (
                    <OracleAdminDashboard onClose={() => setShowAdminDashboard(false)} />
                )}
            </AnimatePresence>
            
            {/* Main Content */}
            <motion.div
                className="relative z-10 pb-24 w-full max-w-lg mx-auto px-safe"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8 }}
            >
                {/* User Avatar - Settings Trigger - Part of page content (scrolls away) */}
                <div className="flex justify-end pt-4 px-4">
                    <button
                        onClick={handleOpenSettings}
                        className="w-10 h-10 rounded-full bg-black/50 border-2 border-primary/40 hover:border-primary/70 transition-all overflow-hidden group"
                        data-testid="settings-btn"
                    >
                        {localAvatarUrl ? (
                            <img 
                                src={localAvatarUrl} 
                                alt="Profile" 
                                className="w-full h-full object-cover"
                            />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/20 to-primary/5">
                                <svg className="w-5 h-5 text-primary/60 group-hover:text-primary transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                </svg>
                            </div>
                        )}
                    </button>
                </div>

                {/* 1. HEADER: Cosmic Identity */}
                <CosmicIdentityHeader 
                    userData={userData} 
                    onReset={onReset} 
                    onLogout={onLogout}
                    isAuthenticated={isAuthenticated}
                />
                
                {/* WELCOME OMEN: DISABLED FOR NOW - Keeping code for future use
                 * The Oracle's Daily Message - Gold on Obsidian
                 * To re-enable: uncomment the line below
                 */}
                {/* <WelcomeOmen userName={userData?.name} /> */}
                
                {/* CELESTIAL MESSENGER: The Oracle's Welcome */}
                <CelestialMessenger 
                    userName={userData?.name}
                    sunSign={userData?.sunSign}
                    gender={userData?.genderIdentity || userData?.gender}
                    onEnterChamber={(oracleType, event) => {
                        // Navigate to the appropriate oracle chat
                        if (onOpenChat) {
                            onOpenChat(oracleType, { celestialEvent: event });
                        }
                    }}
                    className="mb-4"
                />
                
                {/* TRANSIT HORIZON: Celestial Events Arc */}
                <TransitHorizon 
                    onEventClick={(event) => {
                        console.log('Transit event clicked:', event);
                    }}
                    className="mb-4 bg-black/40 border border-primary/10 rounded-xl p-4"
                />
                
                {/* Scrollable Content */}
                <div className="space-y-2">
                    {/* 1. TOP PRIORITY: Sacred Space (Dreams/Love Chat) - Loads immediately */}
                    <DreamSanctuary />
                    
                    {/* 2. ARCHITECT OF THE YEAR - Lazy loaded */}
                    <LazySection height={180} delay={0}>
                        <ArchitectOfYear 
                            birthDate={userData?.birthDate} 
                            birthTime={userData?.birthTime}
                            birthLat={userData?.locationLat}
                            birthLng={userData?.locationLng}
                            userName={userData?.name} 
                            ascendantSign={userData?.ascendantSign} 
                        />
                    </LazySection>
                    
                    {/* 3. The Daily Current (Split Card) - Lazy loaded */}
                    <LazySection height={200} delay={50}>
                        <DailyCurrentCard birthDate={userData?.birthDate} />
                    </LazySection>
                    
                    {/* 4. The Oracle (Horoscope) - Lazy loaded */}
                    <LazySection height={300} delay={100}>
                        <OracleSection />
                    </LazySection>
                    
                    {/* 5. The 12 Houses (Horizontal Carousel) - Lazy loaded */}
                    <LazySection height={250} delay={150}>
                        <TwelveHousesCarousel 
                            birthDate={userData?.birthDate}
                            birthTime={userData?.birthTime}
                            ascendantSign={userData?.ascendantSign}
                            userName={userData?.name}
                        />
                    </LazySection>
                    
                    {/* 6. Your Orbit (Social Relationships) - Lazy loaded */}
                    <LazySection height={200} delay={200}>
                        <OrbitSection userData={userData} isAuthenticated={isAuthenticated} />
                    </LazySection>
                    
                    {/* ============================================================
                     * HIDDEN FOR NOW - CONSULT MIRA BUTTON
                     * Keep this code for future use when chat feature is ready
                     * ============================================================
                    {onOpenChat && (
                        <motion.div 
                            className="px-4"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.6 }}
                        >
                            <button
                                onClick={onOpenChat}
                                className="w-full py-4 rounded-lg font-inter text-sm uppercase tracking-widest"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.15) 0%, rgba(212, 175, 55, 0.05) 100%)',
                                    border: '1px solid rgba(212, 175, 55, 0.3)',
                                    color: '#D4AF37',
                                }}
                                data-testid="open-mira-chat"
                            >
                                Consult MIRA
                            </button>
                        </motion.div>
                    )}
                    ============================================================ */}
                </div>
                
                {/* Push Notification Prompt - Shows after 3 seconds on dashboard */}
                <NotificationPrompt 
                    onEnabled={() => console.log('📬 Notifications enabled!')}
                />
                
                {/* Floating Carta Button */}
                <motion.button
                    onClick={() => setShowNatalChart(true)}
                    className="fixed bottom-24 right-4 z-40 w-12 h-12 rounded-full flex items-center justify-center shadow-lg"
                    style={{
                        background: 'linear-gradient(135deg, rgba(212,175,55,0.9) 0%, rgba(212,175,55,0.7) 100%)',
                        boxShadow: '0 4px 20px rgba(212,175,55,0.4)'
                    }}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1 }}
                    data-testid="natal-chart-btn"
                    title={isSpanish ? 'Mi Carta Natal' : 'My Birth Chart'}
                >
                    <span className="text-xl text-black">✧</span>
                </motion.button>
            </motion.div>
            
            {/* Natal Chart Modal */}
            <AnimatePresence>
                {showNatalChart && (
                    <NatalChart
                        isOpen={showNatalChart}
                        onClose={() => setShowNatalChart(false)}
                        onOpenOracle={(oracleType, context) => {
                            setShowNatalChart(false);
                            if (onOpenChat) onOpenChat(oracleType, context);
                        }}
                        userData={userData}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};

export default SanctuaryDashboard;
